import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { FaBell } from "react-icons/fa";
import { buildInvoiceFollowupHoverTip } from "../../utilis/invoiceFollowupHelpers";

const TIP_MAX_WIDTH = 240;
const TIP_GAP = 8;
const TIP_EST_HEIGHT = 140;

function FollowupTipContent({ tip }) {
  return (
    <>
      {tip.title ? (
        <span className="client-invoices-xero__followup-tip-title">
          {tip.title}
        </span>
      ) : null}
      {tip.sections.map((section) => (
        <span
          key={section.key}
          className="client-invoices-xero__followup-tip-section"
        >
          <span
            className={`client-invoices-xero__followup-tip-heading client-invoices-xero__followup-tip-heading--${section.key}`}
          >
            {section.label}
          </span>
          <ul className="client-invoices-xero__followup-tip-list">
            {section.items.map((item) => (
              <li key={item.key}>{item.label}</li>
            ))}
          </ul>
        </span>
      ))}
      {tip.footer ? (
        <span className="client-invoices-xero__followup-tip-footer">
          {tip.footer}
        </span>
      ) : null}
    </>
  );
}

function computeTipStyle(anchorRect, tipWidth, tipHeight) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(Math.max(tipWidth, 1), TIP_MAX_WIDTH);
  const height = Math.max(tipHeight, 1);

  let left = anchorRect.left + anchorRect.width / 2 - width / 2;
  left = Math.max(TIP_GAP, Math.min(left, vw - width - TIP_GAP));

  const spaceAbove = anchorRect.top;
  const spaceBelow = vh - anchorRect.bottom;
  const preferAbove = spaceAbove >= height + TIP_GAP || spaceAbove >= spaceBelow;

  if (preferAbove && spaceAbove >= TIP_GAP) {
    return {
      top: anchorRect.top - TIP_GAP,
      left,
      transform: "translateY(-100%)",
    };
  }

  return {
    top: anchorRect.bottom + TIP_GAP,
    left,
    transform: "none",
  };
}

function InvoiceFollowupListBell({
  count = 0,
  summary = null,
  disabled = false,
  ariaLabel = "Add follow-up",
  onClick,
}) {
  const tip = useMemo(
    () => buildInvoiceFollowupHoverTip(summary),
    [summary],
  );
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipStyle, setTipStyle] = useState(null);

  const updateTipPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    if (anchorRect.width === 0 && anchorRect.height === 0) return;

    const tipEl = tipRef.current;
    const tipWidth = tipEl?.offsetWidth || TIP_MAX_WIDTH;
    const tipHeight = tipEl?.offsetHeight || TIP_EST_HEIGHT;
    const next = computeTipStyle(anchorRect, tipWidth, tipHeight);

    setTipStyle((prev) => {
      if (
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.transform === next.transform
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const openTip = useCallback(() => {
    if (disabled) return;
    setTipOpen(true);
  }, [disabled]);

  const closeTip = useCallback(() => {
    setTipOpen(false);
    setTipStyle(null);
  }, []);

  useLayoutEffect(() => {
    if (!tipOpen) return undefined;

    updateTipPosition();
    const raf = requestAnimationFrame(() => updateTipPosition());

    return () => cancelAnimationFrame(raf);
  }, [tipOpen, updateTipPosition]);

  useEffect(() => {
    if (!tipOpen) return undefined;

    const onReposition = () => updateTipPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [tipOpen, updateTipPosition]);

  const portalTip =
    tipOpen &&
    typeof document !== "undefined"
      ? createPortal(
          <div
            ref={tipRef}
            className="client-invoices-xero__followup-tip client-invoices-xero__followup-tip--portal"
            style={{
              position: "fixed",
              top: tipStyle?.top ?? -9999,
              left: tipStyle?.left ?? -9999,
              transform: tipStyle?.transform ?? "none",
              visibility: tipStyle ? "visible" : "hidden",
              zIndex: 10050,
            }}
            role="tooltip"
          >
            <FollowupTipContent tip={tip} />
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <span
        ref={anchorRef}
        className="client-invoices-xero__followup-wrap"
        onMouseEnter={openTip}
        onMouseLeave={closeTip}
        onFocus={openTip}
        onBlur={closeTip}
      >
        <button
          type="button"
          className="client-invoices-xero__icon-action client-invoices-xero__icon-action--followup client-invoices-xero__icon-action--followup-bell"
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={onClick}
        >
          <FaBell size={13} />
          {count > 0 ? (
            <span
              className="client-invoices-xero__followup-count"
              aria-hidden="true"
            >
              {count > 99 ? "99+" : count}
            </span>
          ) : null}
        </button>
      </span>
      {portalTip}
    </>
  );
}

export default InvoiceFollowupListBell;
